import React, { useMemo } from "react";
import { resolveMediaUrl } from "./mediaUrl";
import { normalizeOrderDetail } from "./commerceMastersApi";
import { normalizeOrderItemStatus } from "./orderItemStatuses";
import { normalizePaymentStatus } from "./paymentStatuses";
import {
  RecordDrawer,
  RecordDrawerBody,
  RecordDrawerFacts,
  RecordDrawerHeader,
  RecordDrawerPill,
  RecordDrawerSection
} from "./RecordDrawer";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function hasText(value) {
  return value != null && String(value).trim() !== "";
}

function summarizeStatuses(statuses = []) {
  const unique = [...new Set(statuses.map(normalizeOrderItemStatus))];
  if (unique.length === 0) return "Pending";
  if (unique.length === 1) return unique[0];
  return "Mixed";
}

function groupOrderLines(details = [], productsById) {
  const groups = new Map();

  for (const raw of details) {
    const line = normalizeOrderDetail(raw) ?? raw;
    const productId = Number(line.productId);
    if (!Number.isFinite(productId)) continue;

    const product = productsById?.get(productId);
    if (!groups.has(productId)) {
      groups.set(productId, {
        productId,
        name: product?.name ?? product?.Name ?? `Product #${productId}`,
        image: product?.image ?? product?.Image ?? "",
        qty: 0,
        unitPrice: Number(line.unitPrice ?? 0),
        grandTotal: 0,
        itemStatuses: []
      });
    }

    const group = groups.get(productId);
    group.qty += 1;
    group.grandTotal += Number(line.grandTotal ?? line.unitPrice ?? 0);
    group.itemStatuses.push(normalizeOrderItemStatus(line.itemStatus ?? line.ItemStatus));
  }

  return Array.from(groups.values()).map((group) => ({
    ...group,
    itemStatus: summarizeStatuses(group.itemStatuses)
  }));
}

function ProductImage({ image, name }) {
  const src = resolveMediaUrl(image);
  if (!src) {
    return (
      <div className="record-drawer__thumb record-drawer__thumb--empty" aria-hidden>
        {String(name || "?").slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return <img src={src} alt="" className="record-drawer__thumb" />;
}

export function OrderDetailModal({ order, details = [], customer, address, location, productsById, onClose }) {
  const items = useMemo(() => groupOrderLines(details, productsById), [details, productsById]);

  if (!order) return null;

  const orderNo = order.orderNo ?? order.OrderNo ?? `#${order.id ?? order.Id}`;
  const paymentStatus = normalizePaymentStatus(order.paymentStatus ?? order.PaymentStatus);
  const paymentMethod = order.paymentMethod ?? order.PaymentMethod ?? "—";
  const itemCount = details.length;
  const customerName = customer?.name ?? (order.customerId != null ? `Customer #${order.customerId}` : "—");
  const shipTo = address?.address ?? address?.Address ?? "—";
  const shipLocation = hasText(location) ? location : "—";
  const delivery = Number(order.deliveryCharges ?? order.DeliveryCharges ?? 0);

  const tags = [
    { label: paymentStatus, variant: paymentStatus === "Completed" ? "green" : "neutral" },
    { label: paymentMethod, variant: "neutral" },
    { label: `${itemCount} ${itemCount === 1 ? "item" : "items"}`, variant: "neutral" }
  ];

  const footer = (
    <>
      <dl className="record-drawer__totals">
        <div>
          <dt>Subtotal</dt>
          <dd>{formatMoney(order.subTotal ?? order.SubTotal)}</dd>
        </div>
        <div>
          <dt>Discount</dt>
          <dd>{formatMoney(order.discount ?? order.Discount)}</dd>
        </div>
        <div>
          <dt>Tax</dt>
          <dd>{formatMoney(order.taxAmount ?? order.TaxAmount)}</dd>
        </div>
        <div>
          <dt>Delivery</dt>
          <dd>{delivery === 0 ? "Free" : formatMoney(delivery)}</dd>
        </div>
      </dl>
      <div className="record-drawer__grand">
        <span>Grand total</span>
        <strong>{formatMoney(order.grandTotal ?? order.GrandTotal)}</strong>
      </div>
    </>
  );

  return (
    <RecordDrawer open ariaLabel={`Order ${orderNo}`} onClose={onClose} footer={footer}>
      <RecordDrawerHeader
        label="Order details"
        title={orderNo}
        subtitle={formatDate(order.date ?? order.Date)}
        onClose={onClose}
        tags={tags}
      />
      <RecordDrawerBody>
        <RecordDrawerSection title="Products" action={`${itemCount} items`}>
          {items.length === 0 ? (
            <p className="record-drawer__muted">No products in this order.</p>
          ) : (
            <div className="record-drawer__table-wrap">
              <table className="record-drawer__table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Status</th>
                    <th className="record-drawer__align-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.productId}>
                      <td>
                        <div className="record-drawer__product">
                          <ProductImage image={item.image} name={item.name} />
                          <span className="record-drawer__product-name">{item.name}</span>
                        </div>
                      </td>
                      <td>{item.qty}</td>
                      <td>
                        <RecordDrawerPill variant="green">{item.itemStatus}</RecordDrawerPill>
                      </td>
                      <td className="record-drawer__align-right">{formatMoney(item.grandTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </RecordDrawerSection>

        <RecordDrawerSection title="Customer">
          <RecordDrawerFacts
            columns={2}
            fields={[
              ["Name", customerName, true],
              ["Email", hasText(customer?.email) ? customer.email : "—"],
              ["Phone", hasText(customer?.phoneNo) ? customer.phoneNo : "—"]
            ]}
          />
        </RecordDrawerSection>

        <RecordDrawerSection title="Delivery">
          <RecordDrawerFacts
            fields={[
              ["Address", hasText(shipTo) ? shipTo : "—", true],
              ["Location", shipLocation],
              ...(hasText(address?.contactPersonName)
                ? [
                    [
                      "Contact",
                      `${address.contactPersonName}${hasText(address?.contactPersonMob) ? ` · ${address.contactPersonMob}` : ""}`
                    ]
                  ]
                : [])
            ]}
          />
        </RecordDrawerSection>

        {hasText(order.remarks ?? order.Remarks) && (
          <RecordDrawerSection title="Remarks">
            <RecordDrawerFacts fields={[["Remarks", order.remarks ?? order.Remarks, true]]} />
          </RecordDrawerSection>
        )}
      </RecordDrawerBody>
    </RecordDrawer>
  );
}
